/**
 * Manage, Campaigns: the send history (plan docs/plans/lms-plan.md 8.2 "the
 * campaign list shows past sends with their counts", 5.4, 18.21; section 13
 * tab 7; WP-43; R6, R8, G9).
 *
 * GET /admin/campaigns, newest first, 50 a page with "Show more", filtered by
 * kind (the wizard's sends, and the retrain and acknowledge campaigns a
 * publish writes, 8.1). Each row: when, the course, the kind, who sent it, how
 * many people it reached and the due date; the course opens the detail.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Megaphone, Send } from 'lucide-react';
import EmptyState from '../../../../ui/EmptyState';
import FilterSelect from '../../../../ui/FilterSelect';
import { primaryBtnCls, secondaryBtnCls } from '../../../../ui/styles';
import { apiErrorMessage } from '../../../api/client';
import { CAMPAIGN_KINDS, listCampaigns } from '../../../api/campaigns';
import type { CampaignKind, CampaignListItem } from '../../../api/campaigns';
import {
  ErrorNote,
  formatDay,
  metaCls,
  rowCls,
  tableCardCls,
  tableCls,
  tbodyCls,
  tdCls,
  thCls,
  theadCls,
} from '../../../components/pickers/shared';
import { ASSIGN_BASE } from '../assign/paths';
import { CAMPAIGN_KIND_LABELS, campaignHref, CampaignKindPill } from './labels';

const PAGE = 50;

export function CampaignList() {
  const [kind, setKind] = useState<CampaignKind | ''>('');
  const [items, setItems] = useState<CampaignListItem[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const request = useRef(0);

  const load = useCallback(
    (offset: number) => {
      const id = ++request.current;
      setLoading(true);
      setError(null);
      listCampaigns({ kind: kind || null, limit: PAGE, offset })
        .then((page) => {
          if (id !== request.current) return;
          const rows = Array.isArray(page.items) ? page.items : [];
          setItems((prev) => (offset === 0 ? rows : [...prev, ...rows]));
          setHasMore(Boolean(page.page?.hasMore));
        })
        .catch((err) => {
          if (id !== request.current) return;
          setError(apiErrorMessage(err, 'Could not load the campaigns.'));
        })
        .finally(() => {
          if (id === request.current) setLoading(false);
        });
    },
    [kind]
  );

  useEffect(() => {
    load(0);
  }, [load]);

  return (
    <div className="space-y-4" data-testid="campaigns-list">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="w-full sm:w-64">
          <FilterSelect
            value={kind}
            onChange={(v) => setKind(v as CampaignKind | '')}
            label="Kind of send"
            options={[{ value: '', label: 'Every kind' }, ...CAMPAIGN_KINDS.map((k) => ({ value: k, label: CAMPAIGN_KIND_LABELS[k] }))]}
          />
        </div>
        <Link to={ASSIGN_BASE} className={primaryBtnCls}>
          <Send className="h-4 w-4" />
          Assign a course
        </Link>
      </div>

      {error && (
        <ErrorNote>
          {error}{' '}
          <button type="button" className="font-medium underline" onClick={() => load(0)}>
            Retry
          </button>
        </ErrorNote>
      )}

      {!error && !loading && items.length === 0 ? (
        <EmptyState
          icon={Megaphone}
          variant="card"
          title={kind ? "No sends of this kind yet" : "Nothing sent yet"}
          hint="Every send from Assign, and every retrain a publish starts, is kept here with who received it."
        />
      ) : (
        items.length > 0 && (
          <div className={tableCardCls}>
            <table className={tableCls}>
              <thead className={theadCls}>
                <tr>
                  <th scope="col" className={thCls}>
                    Sent
                  </th>
                  <th scope="col" className={thCls}>
                    Course
                  </th>
                  <th scope="col" className={thCls}>
                    Kind
                  </th>
                  <th scope="col" className={thCls}>
                    Sent by
                  </th>
                  <th scope="col" className={`${thCls} text-right`}>
                    People
                  </th>
                  <th scope="col" className={thCls}>
                    Due
                  </th>
                </tr>
              </thead>
              <tbody className={tbodyCls}>
                {items.map((c) => (
                  <tr key={c.id} className={rowCls} data-testid={`campaign-row-${c.id}`}>
                    <td className={`${tdCls} whitespace-nowrap`}>{formatDay(c.sent_at)}</td>
                    <td className={tdCls}>
                      <Link
                        to={campaignHref(c.id)}
                        className="font-medium text-primary-700 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400"
                      >
                        {c.course?.title ?? 'A course no longer listed'}
                      </Link>
                      {c.message && <p className={`${metaCls} mt-0.5 line-clamp-1`}>{c.message}</p>}
                    </td>
                    <td className={tdCls}>
                      <CampaignKindPill kind={c.kind} />
                    </td>
                    <td className={tdCls}>{c.sent_by_name ?? <span className={metaCls}>Unknown</span>}</td>
                    <td className={`${tdCls} text-right tabular-nums`}>{(c.recipient_count ?? 0).toLocaleString()}</td>
                    <td className={`${tdCls} whitespace-nowrap`}>{formatDay(c.due_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      <div className="flex items-center gap-2">
        {loading && <Loader2 aria-label="Loading campaigns" className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" />}
        {hasMore && !loading && (
          <button type="button" className={secondaryBtnCls} onClick={() => load(items.length)} data-testid="campaigns-more">
            Show more
          </button>
        )}
      </div>
    </div>
  );
}

export default CampaignList;
